import * as Sentry from "@sentry/react";
import { multiFactor, onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, onSnapshot, query, setDoc, Unsubscribe, where } from "firebase/firestore";
import i18next from 'i18next';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { auth, db } from "../firebaseConfig";
import { Account, AccountType, CalculationSetups, MinimalAccount, User, UserSetting, UserSubscription } from "../global/types";
import { addAccount } from "../services/accountsServices";
import { useLiveUserPlan } from "../services/subscriptionServices";
import { cleanupReportingCurrency, updateUserData, updateUserLanguage } from '../services/usersServices';
import { resolveReportingCurrency } from "../utils/reportingCurrency";
import UnsupportedCurrencyDialog from "../components/UnsupportedCurrencyDialog";
import type { SupportedCurrency } from "@sentola/money";
import { clearImpersonationState } from '../hooks/useImpersonation';
import { persistor } from "../store";
import { setSettings } from "../store/authSlice";
import { setAccounts, setFinancialCategory } from "../store/userDataSlice";
import { createAvatarUrl } from "../utils/stringUtils";
import logger from "../utils/logger";
import { analytics } from "@/utils/analytics";
import { safeAsync } from "@/utils/promiseUtils";


type UserContextType = {
    userId: string | null;
    subscription: UserSubscription | null;
    user: User | null;
    logout: () => Promise<void>;
    isEmailVerified: boolean;
    isPhoneVerified: boolean;
    loading: boolean;
    error?: Error | null;
    isAuthorized: boolean;
};

const TRUSTED_PROVIDERS = ['yandex.ru'];

const defaultContext: UserContextType = {
    userId: null,
    subscription: null,
    user: null,
    logout: async () => {
        throw new Error("logout called without UserProvider");
    },
    isEmailVerified: false,
    isPhoneVerified: false,
    loading: true,
    error: null,
    isAuthorized: false,
};

const UserContext = createContext<UserContextType>(defaultContext);

function UserProvider({ children }: { children: ReactNode }) {
    const dispatch = useDispatch();
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const { data: subscription } = useLiveUserPlan(user?.id || null);
    const [error, setError] = useState<Error | null>(null);
    const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
    const [isPhoneVerified, setIsPhoneVerified] = useState<boolean>(false);
    const [authProvider, setAuthProvider] = useState<string | null>(null);
    const [unsupportedCurrency, setUnsupportedCurrency] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        const extractAuthProvider = async (firebaseUser: any): Promise<string | null> => {
            try {
                // First try to get custom claims from ID token
                const idTokenResult = await firebaseUser.getIdTokenResult();
                if (idTokenResult.claims.provider) {
                    return idTokenResult.claims.provider;
                }

                return null;
            } catch (error) {
                logger.warn("Failed to extract auth provider", error);
                return null;
            }
        };

        const getOrCreateUserData = async (firebaseUser: any) => {
            try {
                const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
                const avatarUrl = createAvatarUrl(firebaseUser.displayName || firebaseUser.email || firebaseUser.uid);

                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    let currentPhotoURL = userData.photoURL;

                    // Sync photoURL if available from provider (Google) and we haven't uploaded a custom one
                    if (firebaseUser.photoURL) {
                        const isCustomUpload = currentPhotoURL?.includes('firebasestorage.googleapis.com');
                        const isGeneratedAvatar = currentPhotoURL?.includes('ui-avatars.com');
                        const isStaleGooglePhoto = currentPhotoURL?.includes('googleusercontent.com') && currentPhotoURL !== firebaseUser.photoURL;

                        // Update if: No photo, Generated Avatar, or Stale Google Photo
                        // AND it's not a custom upload
                        if ((!currentPhotoURL || isGeneratedAvatar || isStaleGooglePhoto) && !isCustomUpload) {
                            await updateUserData(firebaseUser.uid, { photoURL: firebaseUser.photoURL });
                            currentPhotoURL = firebaseUser.photoURL;
                            logger.info("Synced updated profile photo from provider");
                        }
                    }

                    return ({
                        id: firebaseUser.uid,
                        ...userData,
                        photoURL: currentPhotoURL || avatarUrl,
                        authProvider: await extractAuthProvider(firebaseUser),
                    } as User);
                } else {
                    const newUser = {
                        id: firebaseUser.uid,
                        email: firebaseUser.email,
                        fullName: firebaseUser.displayName || null,
                        photoURL: firebaseUser.photoURL || avatarUrl,
                        authProvider: await extractAuthProvider(firebaseUser),
                    };
                    await setDoc(doc(db, "users", firebaseUser.uid), newUser);
                    return (newUser as User);
                }
            } catch (error) {
                logger.error("Error fetching user data:", error);
                return null;
            }
        }

        const getOrCreateSettingsData = async (firebaseUser: any) => {
            const userId = firebaseUser.uid;
            const settingsDoc = await getDoc(doc(db, "settings", userId));
            if (settingsDoc.exists()) {
                return settingsDoc.data() as UserSetting;
            } else {
                const newSettings: UserSetting = {
                    currency: '',
                    defaultTaxRate: 0,
                    startingDate: new Date().toISOString(),
                    calculationSetup: CalculationSetups.ZERO_BASED
                };
                await setDoc(doc(db, "settings", userId), newSettings);
                return newSettings;
            }
        }

        const createCashAccountIfInexistent = async (userId: string) => {
            const accountId = `cash-${userId}`;
            const cashAccountSnapshot = await getDoc(doc(db, "accounts", accountId));

            if (!cashAccountSnapshot.exists()) {
                await addAccount({
                    name: "Wallet",
                    type: AccountType.CASH,
                    subType: "Cash on Hand",
                    adjustments: 0,
                    currency: '',
                    lastChecked: Date.now(),
                    userId: userId,
                    createdAt: Date.now(),
                    updatedAt: Date.now(),
                }, undefined, accountId);
                const createdSnapshot = await getDoc(doc(db, "accounts", accountId));
                return createdSnapshot.data() as Account;
            }
            return cashAccountSnapshot.data() as Account;
        }

        let authTimeout: any;

        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (authTimeout) clearTimeout(authTimeout);

            if (!firebaseUser) {
                // Wait a bit to ensure it's not a transient null state during persistent state loading
                authTimeout = setTimeout(() => {
                    setUser(null);
                    setIsEmailVerified(false);
                    setIsPhoneVerified(false);
                    Sentry.setUser(null);
                    dispatch(setSettings(null));
                    setLoading(false);
                }, 2500); // 2.5s delay to be safe
                return;
            }

            // We have a user, start the loading process for their data
            setLoading(true);

            await safeAsync(async () => {
                const userDataPromise = getOrCreateUserData(firebaseUser);
                const settingsPromise = getOrCreateSettingsData(firebaseUser);
                const cashAccountPromise = createCashAccountIfInexistent(firebaseUser.uid);

                try {
                    const [user, settings] = await Promise.all([
                        userDataPromise,
                        settingsPromise,
                        cashAccountPromise
                    ]);

                    if (!user) throw new Error("User data is null");

                    try {
                        await updateUserLanguage(user.id, i18next.language);
                    } catch (langError) {
                        logger.warn("Failed to update language", langError);
                    }

                    setUser(user);
                    setIsEmailVerified(firebaseUser.emailVerified || !!user?.emailVerified);
                    setIsPhoneVerified(Boolean(
                        multiFactor(firebaseUser).enrolledFactors.length ||
                        user?.mfaEnabled ||
                        user?.phoneVerified
                    ));
                    setAuthProvider(user.authProvider || null);

                    try {
                        analytics.identify(user.id, {
                            email: user.email,
                            name: user.fullName || undefined,
                        });
                    } catch (err) {
                        logger.warn('Analytics identify failed', err);
                    }

                    // Identify user in Sentry
                    Sentry.setUser({
                        id: user.id,
                        email: user.email,
                        username: user.fullName || user.email || 'Anonymous',
                        ip_address: "{{auto}}",
                    });

                    dispatch(setSettings(settings));
                } catch (innerError) {
                    logger.error("Inner init error", innerError);
                    throw innerError;
                } finally {
                    setLoading(false);
                }
            }, (error) => {
                logger.error("Error while saving user data", error || "Unknown Error (Null)");
                setError(error instanceof Error ? error : new Error(String(error)));
                setLoading(false);
            });
        });

        return () => {
            if (authTimeout) clearTimeout(authTimeout);
            unsubscribe();
        }
    }, [dispatch]);

    useEffect(() => {
        if (!user?.id) return;
        const unsubscribes: Unsubscribe[] = [];

        unsubscribes.push(
            onSnapshot(doc(db, "users", user.id), (documentSnapshot) => {
                if (documentSnapshot.exists()) {
                    const updatedUser = { id: documentSnapshot.id, ...documentSnapshot.data() } as User;
                    setUser(updatedUser);
                    if (updatedUser.emailVerified) {
                        setIsEmailVerified(true);
                    }
                    if (updatedUser.phoneVerified || updatedUser.phoneNumber) {
                        setIsPhoneVerified(true);
                    }
                    if (updatedUser.mfaEnabled) {
                        setIsPhoneVerified(true);
                    }
                }
            }, (error) => {
                logger.error("Error listening to user document:", error);
            })
        );

        unsubscribes.push(
            onSnapshot(
                query(collection(db, "accounts"), where("userId", "==", user.id)),
                (querySnapshot) => {
                    const accounts: Account[] = [];
                    querySnapshot.forEach((doc) => {
                        const data = doc.data();
                        accounts.push({
                            id: doc.id,
                            ...data,
                            // Ensure timestamps are serialized to numbers for Redux
                            createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : (data.createdAt || 0),
                            updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : (data.updatedAt || 0),
                            lastChecked: data.lastChecked?.toMillis ? data.lastChecked.toMillis() : (data.lastChecked || 0),
                        } as Account);
                    });
                    dispatch(setAccounts(accounts));
                    logger.debug("Updated accounts:", accounts);
                },
                (error) => {
                    logger.error("Error listening to accounts collection:", error);
                }
            )
        );

        unsubscribes.push(
            onSnapshot(doc(db, "settings", user.id), (documentSnapshot) => {
                if (documentSnapshot.exists()) {
                    const updatedSettings = documentSnapshot.data() as UserSetting;
                    dispatch(setSettings(updatedSettings));

                    const resolution = resolveReportingCurrency(updatedSettings);
                    if ('unsupported' in resolution) {
                        setUnsupportedCurrency(resolution.unsupported);
                    } else {
                        setUnsupportedCurrency(null);
                        // TODO(T18 cleanup): needsCleanup only fires for a leftover toolsCurrency;
                        if ('code' in resolution && resolution.needsCleanup) {
                            cleanupReportingCurrency(user.id, resolution.code)
                                .catch((err) => logger.warn('Failed to clean up a leftover toolsCurrency', err));
                        }
                    }
                }
            }, (error) => {
                logger.error("Error listening to settings document:", error);
            })
        );

        const subcollectionsToWatch = ["income", "debts", "subscriptions", "bills", "expenses", "savings", "spenders"];

        subcollectionsToWatch.forEach(subcollectionName => {
            const subcollectionRef = collection(db, "settings", user.id, subcollectionName);
            unsubscribes.push(
                onSnapshot(query(subcollectionRef), (querySnapshot) => {
                    const items: any[] = [];
                    querySnapshot.forEach((doc) => {
                        const data = doc.data();
                        items.push({
                            id: doc.id,
                            ...data,
                            // Proactively serialize timestamps if they exist
                            createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : (data.createdAt || undefined),
                            updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : (data.updatedAt || undefined),
                        });
                    });

                    dispatch(setFinancialCategory(
                        { type: subcollectionName, data: items }
                    ));

                    logger.debug(`Updated ${subcollectionName}:`, items);
                }, (error) => {
                    logger.error(`Error listening to ${subcollectionName} subcollection:`, error);
                })
            );
        });

        return () => {
            unsubscribes.forEach(unsubscribe => unsubscribe());
            logger.debug("Unsubscribed from all listeners.");
        };
    }, [user?.id, dispatch]);

    const logout = useCallback(async () => {
        safeAsync(async () => {
            await auth.signOut();
            setUser(null);
            Sentry.setUser(null);
            dispatch(setSettings(null));
            persistor.purge();
            analytics.logout();
            clearImpersonationState();
        }, (error) => {
            logger.error('Failed to log out', error);
        });
    }, [dispatch]);

    const isAuthorized = isEmailVerified || isPhoneVerified || (authProvider && TRUSTED_PROVIDERS.includes(authProvider)) || false;

    return (
        <UserContext.Provider value={{
            userId: user?.id || null,
            subscription,
            user,
            isEmailVerified,
            isPhoneVerified,
            logout,
            loading,
            error,
            isAuthorized,
        }}>
            {children}
            <UnsupportedCurrencyDialog
                isOpen={!!unsupportedCurrency}
                attemptedCode={unsupportedCurrency ?? ''}
                onSelect={(code: SupportedCurrency) => {
                    setUnsupportedCurrency(null);
                    if (user?.id) {
                        cleanupReportingCurrency(user.id, code)
                            .catch((err) => logger.warn('Failed to save the chosen reporting currency', err));
                    }
                }}
                onClose={() => setUnsupportedCurrency(null)}
            />
        </UserContext.Provider>
    );
}

const useUserContext = (): UserContextType => {
    const context = useContext(UserContext);
    if (context === defaultContext) {
        throw new Error("useUserContext must be used within a UserProvider");
    }
    return context;
};

export { UserProvider, useUserContext };
